import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  BudgetLine,
  BudgetScenario,
  BudgetVersion,
  CommitmentRecord,
  FinanceApiService
} from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-budgets',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule],
  template: `
    <ain-page title="Budgets and Commitments" description="Scenario/version/line and commitment monitoring workspace">
      @if (!budgetingEnabled()) {
        <ain-state type="empty" title="Budgeting capability is disabled" message="Enable financial-core.budgeting in control center to use this page." />
      } @else {
      <section class="section">
        <h3>Scenarios</h3>
        <p-table [value]="scenarios()">
          <ng-template pTemplate="header"><tr><th>Code</th><th>Name</th></tr></ng-template>
          <ng-template pTemplate="body" let-row><tr><td>{{ row.code }}</td><td>{{ row.name }}</td></tr></ng-template>
        </p-table>
      </section>

      <section class="section">
        <h3>Versions</h3>
        <div class="toolbar">
          <input pInputText [(ngModel)]="scenarioIdFilter" placeholder="Scenario id filter (optional)" />
          <button pButton type="button" label="Refresh" (click)="loadVersions()"></button>
        </div>
        <p-table [value]="versions()">
          <ng-template pTemplate="header"><tr><th>FiscalYear</th><th>Version</th><th>Status</th><th>Scenario</th></tr></ng-template>
          <ng-template pTemplate="body" let-row>
            <tr><td>{{ row.fiscalYear }}</td><td>{{ row.versionNumber }}</td><td>{{ row.status }}</td><td>{{ row.scenarioId }}</td></tr>
          </ng-template>
        </p-table>
      </section>

      <section class="section">
        <h3>Lines</h3>
        <div class="toolbar">
          <input pInputText [(ngModel)]="versionIdFilter" placeholder="Version id filter (optional)" />
          <button pButton type="button" label="Refresh" (click)="loadLines()"></button>
        </div>
        <p-table [value]="lines()">
          <ng-template pTemplate="header"><tr><th>LineCode</th><th>Budget</th><th>Revised</th><th>Version</th></tr></ng-template>
          <ng-template pTemplate="body" let-row>
            <tr><td>{{ row.lineCode }}</td><td>{{ row.budgetAmount }}</td><td>{{ row.revisedAmount }}</td><td>{{ row.versionId }}</td></tr>
          </ng-template>
        </p-table>
      </section>

      <section class="section">
        <h3>Commitments</h3>
        @if (!commitmentsEnabled()) {
          <ain-state type="empty" title="Commitment control is disabled" message="Enable financial-core.commitment-control to view commitments." />
        } @else {
          <p-table [value]="commitments()">
            <ng-template pTemplate="header"><tr><th>Number</th><th>Amount</th><th>Consumed</th><th>Mode</th><th>Status</th></tr></ng-template>
            <ng-template pTemplate="body" let-row>
              <tr><td>{{ row.number }}</td><td>{{ row.amount }}</td><td>{{ row.consumedAmount }}</td><td>{{ row.controlMode }}</td><td>{{ row.status }}</td></tr>
            </ng-template>
          </p-table>
        }
      </section>
      }
    </ain-page>
  `,
  styles: [`
    .section { margin-bottom: 24px; display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; }
  `]
})
export class FinanceBudgetsComponent implements OnInit {
  private readonly api = inject(FinanceApiService);

  readonly scenarios = signal<BudgetScenario[]>([]);
  readonly versions = signal<BudgetVersion[]>([]);
  readonly lines = signal<BudgetLine[]>([]);
  readonly commitments = signal<CommitmentRecord[]>([]);
  readonly budgetingEnabled = signal(false);
  readonly commitmentsEnabled = signal(false);

  scenarioIdFilter = '';
  versionIdFilter = '';

  ngOnInit(): void {
    this.api.getCapabilitySetting('financial-core.budgeting').subscribe({
      next: setting => {
        this.budgetingEnabled.set(setting.isEnabled);
        if (!setting.isEnabled) return;

        this.api.listBudgetScenarios().subscribe({ next: rows => this.scenarios.set(rows) });
        this.loadVersions();
        this.loadLines();
        this.loadCommitments();
      }
    });
  }

  loadVersions(): void {
    const filter = this.scenarioIdFilter.trim();
    this.api.listBudgetVersions(filter || undefined).subscribe({ next: rows => this.versions.set(rows) });
  }

  loadLines(): void {
    const filter = this.versionIdFilter.trim();
    this.api.listBudgetLines(filter || undefined).subscribe({ next: rows => this.lines.set(rows) });
  }

  private loadCommitments(): void {
    this.api.getCapabilitySetting('financial-core.commitment-control').subscribe({
      next: setting => {
        this.commitmentsEnabled.set(setting.isEnabled);
        if (!setting.isEnabled) return;
        this.api.listCommitments().subscribe({ next: rows => this.commitments.set(rows) });
      }
    });
  }
}
